/**
 * ConferenceNetworkPage - Public-facing conference network page
 * 
 * Layout:
 * - Top bar: Member school logos (linked to athletic websites)
 * - Header: Conference logo (left) + Navigation menu (right)
 * - Content: Varies by view (home, schedule, standings, scores, multiview)
 */
import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import { Calendar, Clock, MapPin, Play, ChevronRight, ChevronDown, ChevronUp, Grid, X, Maximize2, Minimize2, Tv } from "lucide-react";
import axios from "axios";
import { VideoAdPlayer } from "../components/VideoAdPlayer";
import { CastButton, MultiViewCastButton } from "../components/CastButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ConferenceNetworkPage = () => {
  const { networkSlug } = useParams();
  const location = useLocation();
  const [network, setNetwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveGames, setLiveGames] = useState([]);
  const [nextGames, setNextGames] = useState([]);
  const [allSchedule, setAllSchedule] = useState([]);
  const [completedGames, setCompletedGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [currentView, setCurrentView] = useState("home");
  
  // Multi-view state
  const [multiViewGames, setMultiViewGames] = useState([]);
  const [multiViewLayout, setMultiViewLayout] = useState(2); // 2 or 4 games
  
  // Standings state
  const [standings, setStandings] = useState([]);
  const [standingsLoading, setStandingsLoading] = useState(false);

  useEffect(() => {
    fetchNetwork();
  }, [networkSlug]);
  
  // Fetch standings when entering standings view
  useEffect(() => {
    if (currentView === "standings" && network) {
      fetchStandings();
    }
  }, [currentView, network]);

  useEffect(() => {
    const path = location.pathname;
    if (path.includes("/schedule")) {
      setCurrentView("schedule");
    } else if (path.includes("/standings")) {
      setCurrentView("standings");
    } else if (path.includes("/scores")) {
      setCurrentView("scores");
    } else if (path.includes("/multiview")) {
      setCurrentView("multiview");
    } else {
      setCurrentView("home");
    }
  }, [location.pathname]);

  const fetchNetwork = async () => {
    try {
      const [networkRes, liveRes, nextRes, scheduleRes, resultsRes] = await Promise.all([
        axios.get(`${API}/conference-networks/${networkSlug}`),
        axios.get(`${API}/conference-networks/${networkSlug}/live`),
        axios.get(`${API}/conference-networks/${networkSlug}/next?limit=3`),
        axios.get(`${API}/conference-networks/${networkSlug}/schedule?limit=50`),
        axios.get(`${API}/conference-networks/${networkSlug}/results?limit=20`).catch(() => ({ data: [] }))
      ]);
      
      setNetwork(networkRes.data);
      setLiveGames(liveRes.data);
      setNextGames(nextRes.data);
      setAllSchedule(scheduleRes.data);
      setCompletedGames(resultsRes.data);
      
      // Only auto-select if there's exactly 1 live game OR no live games (pick first upcoming)
      // If multiple live games exist, show the game selector screen instead
      if (liveRes.data.length === 1) {
        setSelectedGame(liveRes.data[0]);
        setMultiViewGames(liveRes.data);
      } else if (liveRes.data.length === 0 && nextRes.data.length > 0) {
        setSelectedGame(nextRes.data[0]);
      } else if (liveRes.data.length > 1) {
        // Multiple live games - don't auto-select, show game selector screen
        setSelectedGame(null);
        setMultiViewGames(liveRes.data.slice(0, 4));
      }
    } catch (err) {
      console.error("Error fetching conference network:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStandings = async () => {
    if (!network?.channel_id) return;
    setStandingsLoading(true);
    try {
      const res = await axios.get(`${API}/standings/conference/${network.channel_id}`);
      setStandings(res.data || []);
    } catch (err) {
      console.error("Error fetching standings:", err);
      setStandings([]);
    } finally {
      setStandingsLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const toggleMultiViewGame = (game) => {
    if (multiViewGames.find(g => g.id === game.id)) {
      setMultiViewGames(multiViewGames.filter(g => g.id !== game.id));
    } else if (multiViewGames.length < 4) {
      setMultiViewGames([...multiViewGames, game]);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!network) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-center">
          <h1 className="text-2xl font-bold mb-2">Network Not Found</h1>
          <p className="text-zinc-400">The conference network you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  const primaryColor = network.primary_color || "#f97316";
  const secondaryColor = network.secondary_color || "#000000";
  const memberSchools = network.member_schools || [];
  const menuItems = network.menu_items || [];

  return (
    <div className="min-h-screen bg-black">
      {/* Member Schools Bar */}
      {memberSchools.length > 0 && (
        <div 
          className="w-full py-2 px-4 overflow-x-auto"
          style={{ backgroundColor: secondaryColor }}
        >
          <div className="flex items-center justify-center gap-4 min-w-max mx-auto">
            {memberSchools
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((school) => (
                <a
                  key={school.id}
                  href={school.athletic_website || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-shrink-0 transition-transform hover:scale-110"
                  title={school.name}
                >
                  {school.logo_url ? (
                    <img
                      src={school.logo_url}
                      alt={school.name}
                      className="h-10 w-10 object-contain"
                    />
                  ) : (
                    <div 
                      className="h-10 w-10 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: primaryColor }}
                    >
                      {school.abbreviation || school.name?.slice(0, 2).toUpperCase()}
                    </div>
                  )}
                </a>
              ))}
          </div>
        </div>
      )}

      {/* Header with Logo and Navigation */}
      <header 
        className="w-full py-4 px-6 border-b border-white/10"
        style={{ backgroundColor: `${primaryColor}15` }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to={`/network/${networkSlug}`} className="flex items-center gap-3">
            {network.logo_url ? (
              <img src={network.logo_url} alt={network.name} className="h-12 object-contain" />
            ) : (
              <div 
                className="h-12 px-4 rounded flex items-center justify-center text-white font-bold text-xl"
                style={{ backgroundColor: primaryColor }}
              >
                {network.name}
              </div>
            )}
          </Link>

          {/* Navigation Menu */}
          <nav className="hidden md:flex items-center gap-6">
            {/* Back to ASN link */}
            <Link
              to="/"
              className="text-white/80 hover:text-white text-sm font-medium uppercase tracking-wider transition-colors flex items-center gap-1"
              data-testid="back-to-asn"
            >
              <ChevronRight className="w-3 h-3 rotate-180" />
              ASN
            </Link>
            
            <span className="text-white/30">|</span>
            
            {/* Watch link - goes back to home (main player) */}
            <Link
              to={`/network/${networkSlug}`}
              className={`text-sm font-medium uppercase tracking-wider transition-colors ${
                currentView === "home" ? "text-white border-b-2" : "text-white/80 hover:text-white"
              }`}
              style={{ borderColor: currentView === "home" ? primaryColor : "transparent" }}
            >
              Watch
            </Link>
            
            {menuItems
              .filter(item => item.href !== "/") // Filter out duplicate home link
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((item) => {
                const isActive = 
                  (item.href === "/schedule" && currentView === "schedule") ||
                  (item.href === "/standings" && currentView === "standings") ||
                  (item.href === "/scores" && currentView === "scores");

                const href = item.type === "external" 
                  ? item.href 
                  : `/network/${networkSlug}${item.href}`;

                return item.type === "external" ? (
                  <a
                    key={item.id}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/80 hover:text-white text-sm font-medium uppercase tracking-wider transition-colors"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.id}
                    to={href}
                    className={`text-sm font-medium uppercase tracking-wider transition-colors ${
                      isActive ? "text-white border-b-2" : "text-white/80 hover:text-white"
                    }`}
                    style={{ borderColor: isActive ? primaryColor : "transparent" }}
                  >
                    {item.label}
                  </Link>
                );
              })}
            
            {/* Multi-View Button */}
            {liveGames.length > 1 && (
              <Link
                to={`/network/${networkSlug}/multiview`}
                className={`flex items-center gap-1 text-sm font-medium uppercase tracking-wider transition-colors ${
                  currentView === "multiview" ? "text-white" : "text-white/80 hover:text-white"
                }`}
              >
                <Grid className="w-4 h-4" />
                Multi-View
              </Link>
            )}
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        
        {/* HOME VIEW */}
        {currentView === "home" && (
          <>
            {/* Game Selector Screen - Shows when multiple games are live and no game is selected */}
            {liveGames.length > 1 && !selectedGame && (
              <div className="mb-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-white mb-2">Multiple Games Live Now</h2>
                  <p className="text-zinc-400">Select a game to watch or use Multi-View to watch multiple games simultaneously</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                  {liveGames.map((game) => (
                    <button
                      key={game.id}
                      onClick={() => setSelectedGame(game)}
                      className="bg-zinc-900 border-2 border-white/10 hover:border-orange-500/50 rounded-lg overflow-hidden transition-all group"
                      data-testid={`game-selector-${game.id}`}
                    >
                      {/* Thumbnail / Preview */}
                      <div className="aspect-video bg-zinc-800 relative">
                        {game.thumbnail_url ? (
                          <img src={game.thumbnail_url} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Play className="w-12 h-12 text-zinc-600 group-hover:text-orange-500 transition-colors" />
                          </div>
                        )}
                        <div 
                          className="absolute top-3 left-3 px-2 py-1 text-xs font-bold uppercase rounded flex items-center gap-1"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          LIVE
                        </div>
                      </div>
                      
                      {/* Game Info */}
                      <div className="p-4 text-left">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            {game.away_team?.logo_url && (
                              <img src={game.away_team.logo_url} alt="" className="w-8 h-8 object-contain" />
                            )}
                            <span className="text-white font-medium">{game.away_team?.abbreviation || "AWY"}</span>
                          </div>
                          <span className="text-zinc-500 text-xs">VS</span>
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{game.home_team?.abbreviation || "HME"}</span>
                            {game.home_team?.logo_url && (
                              <img src={game.home_team.logo_url} alt="" className="w-8 h-8 object-contain" />
                            )}
                          </div>
                        </div>
                        <p className="text-zinc-400 text-sm truncate">{game.title}</p>
                        <p className="text-zinc-500 text-xs mt-1">{game.sport}</p>
                      </div>
                    </button>
                  ))}
                </div>
                
                {/* Multi-View CTA */}
                <div className="text-center">
                  <Link
                    to={`/network/${networkSlug}/multiview`}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded transition-colors"
                    data-testid="multiview-cta"
                  >
                    <Grid className="w-5 h-5" />
                    Watch All {liveGames.length} Games in Multi-View
                  </Link>
                </div>
              </div>
            )}

            {/* Video Player - Shows when a game is selected OR only 1 live game OR no live games */}
            {(selectedGame || liveGames.length <= 1) && (
              <div className="mb-8">
                {selectedGame ? (
                  <div className="relative">
                    {/* Team vs Team Banner - Shows only when watching a single game */}
                    <div 
                      className="mb-4 py-4 px-6 rounded-lg flex items-center justify-between"
                      style={{ backgroundColor: `${primaryColor}15`, borderLeft: `4px solid ${primaryColor}` }}
                      data-testid="game-banner"
                    >
                      <div className="flex items-center gap-4">
                        {selectedGame.away_team?.logo_url ? (
                          <img src={selectedGame.away_team.logo_url} alt="" className="w-12 h-12 object-contain" />
                        ) : (
                          <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center text-white font-bold">
                            {selectedGame.away_team?.abbreviation || "AWY"}
                          </div>
                        )}
                        <div>
                          <p className="text-white font-bold text-lg">
                            {selectedGame.away_team?.name || selectedGame.away_team?.abbreviation || "Away Team"}
                          </p>
                          {selectedGame.away_team?.mascot && (
                            <p className="text-zinc-400 text-sm">{selectedGame.away_team.mascot}</p>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-center px-6">
                        <span className="text-zinc-500 text-2xl font-bold">VS</span>
                        {selectedGame.is_live && (
                          <div 
                            className="mt-1 px-2 py-0.5 text-xs font-bold uppercase rounded flex items-center justify-center gap-1"
                            style={{ backgroundColor: primaryColor }}
                          >
                            <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                            LIVE
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-white font-bold text-lg">
                            {selectedGame.home_team?.name || selectedGame.home_team?.abbreviation || "Home Team"}
                          </p>
                          {selectedGame.home_team?.mascot && (
                            <p className="text-zinc-400 text-sm">{selectedGame.home_team.mascot}</p>
                          )}
                        </div>
                        {selectedGame.home_team?.logo_url ? (
                          <img src={selectedGame.home_team.logo_url} alt="" className="w-12 h-12 object-contain" />
                        ) : (
                          <div className="w-12 h-12 bg-zinc-700 rounded-full flex items-center justify-center text-white font-bold">
                            {selectedGame.home_team?.abbreviation || "HME"}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Video Player - Reduced size (2/3 of original) */}
                    <div className="aspect-[16/7] bg-zinc-900 rounded-lg overflow-hidden relative max-w-4xl mx-auto">
                      {/* LIVE Badge - Top Left */}
                      {selectedGame.is_live && (
                        <div 
                          className="absolute top-4 left-4 px-3 py-1 text-xs font-bold uppercase rounded z-10 flex items-center gap-1"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
                          LIVE
                        </div>
                      )}
                      
                      {/* Conference Network Logo/Abbreviation - Top Right */}
                      <div className="absolute top-4 right-4 z-10" data-testid="conference-logo-overlay">
                        {network?.logo_url ? (
                          <img 
                            src={network.logo_url}
                            alt={network.name}
                            className="h-10 w-auto object-contain drop-shadow-lg"
                            style={{ background: 'transparent' }}
                          />
                        ) : (
                          <div 
                            className="px-2 py-1 rounded font-bold text-white text-xs uppercase tracking-wider"
                            style={{ backgroundColor: primaryColor }}
                          >
                            {network?.abbreviation || network?.name?.slice(0, 4).toUpperCase() || "NET"}
                          </div>
                        )}
                      </div>
                      
                      {/* Video content wrapped with VideoAdPlayer */}
                      <VideoAdPlayer 
                        channelId={network?.channel_id}
                        showMidRollAfter={1500}
                        midRollIntervalMin={1500}
                        midRollIntervalMax={2700}
                      >
                        {selectedGame.vcloud_url ? (
                          <div className="w-full h-full">
                            <iframe src={`${selectedGame.vcloud_url}${selectedGame.vcloud_url.includes('?') ? '&' : '?'}autoplay=1`} className="w-full h-full" allowFullScreen allow="autoplay; fullscreen" />
                          </div>
                        ) : selectedGame.youtube_url ? (
                          <div className="w-full h-full">
                            <iframe src={`https://www.youtube.com/embed/${extractYouTubeId(selectedGame.youtube_url)}?autoplay=1&mute=1`} className="w-full h-full" allowFullScreen allow="autoplay; fullscreen" />
                          </div>
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center text-white">
                            <Play className="w-12 h-12 mb-4 opacity-50" />
                            <p className="text-lg font-medium">{selectedGame.title}</p>
                            <p className="text-zinc-400 text-sm mt-2">
                              {formatDate(selectedGame.scheduled_date)} at {formatTime(selectedGame.scheduled_time)}
                            </p>
                          </div>
                        )}
                      </VideoAdPlayer>
                      
                      {/* Video Controls */}
                      <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2">
                        {/* Cast Button */}
                        <CastButton 
                          videoUrl={selectedGame.youtube_url || selectedGame.vcloud_url}
                          title={selectedGame.title || 'Live Stream'}
                          thumbnailUrl={selectedGame.thumbnail_url}
                          className="bg-black/80 hover:bg-black"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* No game selected - show fallback video or placeholder */
                  <div className="aspect-[16/7] bg-zinc-900 rounded-lg overflow-hidden relative max-w-4xl mx-auto">
                    {/* Conference Network Logo/Abbreviation - Top Right */}
                    <div className="absolute top-4 right-4 z-10" data-testid="conference-logo-overlay-empty">
                      {network?.logo_url ? (
                        <img 
                          src={network.logo_url}
                          alt={network.name}
                          className="h-10 w-auto object-contain drop-shadow-lg"
                          style={{ background: 'transparent' }}
                        />
                      ) : (
                        <div 
                          className="px-2 py-1 rounded font-bold text-white text-xs uppercase tracking-wider"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {network?.abbreviation || network?.name?.slice(0, 4).toUpperCase() || "NET"}
                        </div>
                      )}
                    </div>
                    
                    {/* Fallback video loop or placeholder */}
                    {network?.fallback_video_url ? (
                      <div className="w-full h-full">
                        {network.fallback_video_url.includes('youtube.com') || network.fallback_video_url.includes('youtu.be') ? (
                          <iframe 
                            src={`https://www.youtube.com/embed/${extractYouTubeId(network.fallback_video_url)}?autoplay=1&mute=1&loop=1&playlist=${extractYouTubeId(network.fallback_video_url)}`}
                            className="w-full h-full" 
                            allowFullScreen 
                            allow="autoplay; fullscreen" 
                          />
                        ) : (
                          <video 
                            src={network.fallback_video_url} 
                            className="w-full h-full object-cover" 
                            autoPlay 
                            loop 
                            muted 
                            playsInline
                          />
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="text-center text-white">
                          <Play className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p className="text-lg">No live events</p>
                          <p className="text-zinc-500 text-sm mt-1">Check the schedule for upcoming games</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Other Live Games - quick switch when watching a game */}
            {selectedGame && liveGames.length > 1 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-3 flex items-center gap-2">
                  <span className="w-1 h-5 rounded" style={{ backgroundColor: primaryColor }} />
                  OTHER LIVE GAMES
                </h3>
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {liveGames.filter(g => g.id !== selectedGame.id).map((game) => (
                    <button
                      key={game.id}
                      onClick={() => setSelectedGame(game)}
                      className="flex-shrink-0 bg-zinc-900 border border-white/10 hover:border-orange-500/50 rounded-lg p-3 min-w-[200px] text-left transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span 
                          className="px-2 py-0.5 text-[10px] font-bold uppercase rounded flex items-center gap-1"
                          style={{ backgroundColor: primaryColor }}
                        >
                          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                          LIVE
                        </span>
                      </div>
                      <p className="text-white text-sm font-medium truncate">{game.title}</p>
                      <p className="text-zinc-500 text-xs">{game.sport}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Up Next */}
            {nextGames.length > 0 && (
              <div>
                <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <span className="w-1 h-6 rounded" style={{ backgroundColor: primaryColor }} />
                  UP NEXT
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {nextGames.map((game) => (
                    <GameCard 
                      key={game.id} 
                      game={game} 
                      primaryColor={primaryColor}
                      selected={selectedGame?.id === game.id}
                      onClick={() => setSelectedGame(game)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* SCHEDULE VIEW */}
        {currentView === "schedule" && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-1 h-8 rounded" style={{ backgroundColor: primaryColor }} />
              BROADCAST SCHEDULE
            </h2>
            
            {allSchedule.length === 0 ? (
              <div className="text-center py-12 text-zinc-400">
                <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No upcoming broadcasts scheduled</p>
              </div>
            ) : (
              <div className="space-y-4">
                {allSchedule.map((game) => (
                  <div 
                    key={game.id}
                    className="bg-zinc-900 border border-white/10 p-4 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="text-center min-w-[80px]">
                        <p className="text-white font-bold">{formatDate(game.scheduled_date)}</p>
                        <p className="text-zinc-400 text-sm">{formatTime(game.scheduled_time)}</p>
                      </div>
                      <div className="border-l border-white/10 pl-4">
                        <p className="text-white font-medium">{game.title}</p>
                        <span 
                          className="inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {game.sport || "Event"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {game.is_live && (
                        <span className="px-2 py-1 text-xs font-bold uppercase rounded text-white" style={{ backgroundColor: primaryColor }}>
                          LIVE
                        </span>
                      )}
                      {(game.vcloud_url || game.youtube_url) && (
                        <button
                          onClick={() => { setSelectedGame(game); setCurrentView("home"); }}
                          className="px-3 py-1 text-sm text-white hover:bg-white/10 rounded"
                        >
                          Watch
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* STANDINGS VIEW */}
        {currentView === "standings" && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-1 h-8 rounded" style={{ backgroundColor: primaryColor }} />
              STANDINGS
            </h2>
            
            {standingsLoading ? (
              <div className="flex justify-center py-12">
                <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: primaryColor }} />
              </div>
            ) : standings.length === 0 ? (
              <div className="bg-zinc-900 border border-white/10 p-8 text-center">
                <p className="text-zinc-400">No standings configured yet</p>
                <p className="text-zinc-500 text-sm mt-2">Conference standings will be displayed when configured by the administrator</p>
              </div>
            ) : (
              <div className="space-y-3">
                {standings.map((sportStandings) => (
                  <StandingsDropdown 
                    key={sportStandings.sport} 
                    sportStandings={sportStandings} 
                    primaryColor={primaryColor}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* SCORES VIEW */}
        {currentView === "scores" && (
          <div>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-1 h-8 rounded" style={{ backgroundColor: primaryColor }} />
              SCORES
            </h2>
            
            {liveGames.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-4">LIVE NOW</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {liveGames.map((game) => (
                    <ScoreCard key={game.id} game={game} primaryColor={primaryColor} />
                  ))}
                </div>
              </div>
            )}

            {completedGames.length > 0 ? (
              <div>
                <h3 className="text-lg font-bold text-white mb-4">RESULTS</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {completedGames.map((game) => (
                    <ScoreCard key={game.id} game={game} primaryColor={primaryColor} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="bg-zinc-900 border border-white/10 p-8 text-center">
                <p className="text-zinc-400">No completed games yet</p>
              </div>
            )}
          </div>
        )}

        {/* MULTI-VIEW */}
        {currentView === "multiview" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Grid className="w-6 h-6" style={{ color: primaryColor }} />
                MULTI-VIEW
              </h2>
              <div className="flex items-center gap-4">
                {/* Cast to TV Button */}
                <MultiViewCastButton
                  videoUrls={multiViewGames.map(g => g.youtube_url || g.vcloud_url).filter(Boolean)}
                  titles={multiViewGames.map(g => g.title || 'Stream')}
                />
                
                <div className="flex items-center gap-2">
                  <span className="text-zinc-400 text-sm">Layout:</span>
                  <button
                    onClick={() => setMultiViewLayout(2)}
                    className={`px-3 py-1 text-sm rounded ${multiViewLayout === 2 ? 'bg-white text-black' : 'bg-zinc-800 text-white'}`}
                  >
                    2 Games
                  </button>
                  <button
                    onClick={() => setMultiViewLayout(4)}
                    className={`px-3 py-1 text-sm rounded ${multiViewLayout === 4 ? 'bg-white text-black' : 'bg-zinc-800 text-white'}`}
                  >
                    4 Games
                  </button>
                </div>
              </div>
            </div>

            {/* Multi-View Grid */}
            <div className={`grid gap-4 mb-8 ${multiViewLayout === 2 ? 'grid-cols-2' : 'grid-cols-2'}`}>
              {multiViewGames.slice(0, multiViewLayout).map((game, index) => (
                <div key={game.id} className="relative bg-zinc-900 rounded-lg overflow-hidden">
                  {/* Conference Network Logo - Top Right */}
                  {/* Conference Network Logo/Abbreviation - Top Left */}
                  <div className="absolute top-2 left-2 z-10" data-testid="multiview-conference-logo">
                    {network?.logo_url ? (
                      <img 
                        src={network.logo_url}
                        alt={network.name}
                        className="h-8 w-auto object-contain drop-shadow-lg"
                        style={{ background: 'transparent' }}
                      />
                    ) : (
                      <div 
                        className="px-2 py-1 rounded font-bold text-white text-xs uppercase tracking-wider"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {network?.abbreviation || network?.name?.slice(0, 4).toUpperCase() || "NET"}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => toggleMultiViewGame(game)}
                    className="absolute top-2 right-2 z-10 p-1 bg-black/50 rounded text-white hover:bg-black/70"
                  >
                    <X className="w-4 h-4" />
                  </button>
                  <div className={multiViewLayout === 2 ? "aspect-video" : "aspect-video"}>
                    {game.vcloud_url ? (
                      <iframe src={game.vcloud_url} className="w-full h-full" allowFullScreen />
                    ) : game.youtube_url ? (
                      <iframe src={`https://www.youtube.com/embed/${extractYouTubeId(game.youtube_url)}?autoplay=0&mute=1`} className="w-full h-full" allowFullScreen />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white">
                        <p>{game.title}</p>
                      </div>
                    )}
                  </div>
                  <div className="p-2 bg-black/50">
                    <p className="text-white text-sm font-medium truncate">{game.title}</p>
                  </div>
                </div>
              ))}
              
              {/* Empty slots */}
              {Array.from({ length: Math.max(0, multiViewLayout - multiViewGames.length) }).map((_, i) => (
                <div key={`empty-${i}`} className="aspect-video bg-zinc-900 rounded-lg flex items-center justify-center border-2 border-dashed border-white/20 relative">
                  {/* Conference Network Logo/Abbreviation - Top Left (even in empty slots) */}
                  <div className="absolute top-2 left-2 z-10 opacity-50">
                    {network?.logo_url ? (
                      <img 
                        src={network.logo_url}
                        alt={network.name}
                        className="h-8 w-auto object-contain"
                        style={{ background: 'transparent' }}
                      />
                    ) : (
                      <div 
                        className="px-2 py-1 rounded font-bold text-white text-xs uppercase tracking-wider"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {network?.abbreviation || network?.name?.slice(0, 4).toUpperCase() || "NET"}
                      </div>
                    )}
                  </div>
                  <p className="text-zinc-500 text-sm">Select a game</p>
                </div>
              ))}
            </div>

            {/* Game Selector */}
            <div>
              <h3 className="text-lg font-bold text-white mb-4">SELECT GAMES</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[...liveGames, ...nextGames.filter(g => g.vcloud_url || g.youtube_url)].map((game) => {
                  const isSelected = multiViewGames.find(g => g.id === game.id);
                  return (
                    <button
                      key={game.id}
                      onClick={() => toggleMultiViewGame(game)}
                      disabled={!isSelected && multiViewGames.length >= multiViewLayout}
                      className={`p-3 rounded text-left transition-all ${
                        isSelected 
                          ? 'bg-orange-500/20 border-2 border-orange-500' 
                          : multiViewGames.length >= multiViewLayout
                            ? 'bg-zinc-900 border border-white/10 opacity-50 cursor-not-allowed'
                            : 'bg-zinc-900 border border-white/10 hover:border-white/30'
                      }`}
                    >
                      <p className="text-white text-sm font-medium truncate">{game.title}</p>
                      <p className="text-zinc-400 text-xs">{game.sport}</p>
                      {game.is_live && (
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs font-bold rounded" style={{ backgroundColor: primaryColor }}>
                          LIVE
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Standings Dropdown Component
const StandingsDropdown = ({ sportStandings, primaryColor }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  return (
    <div className="bg-zinc-900 border border-white/10 rounded-lg overflow-hidden">
      {/* Clickable Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors"
        style={{ backgroundColor: isExpanded ? `${primaryColor}20` : 'transparent' }}
        data-testid={`standings-dropdown-${sportStandings.sport}`}
      >
        <span className="font-bold text-white uppercase tracking-wider text-lg">
          {sportStandings.sport}
        </span>
        <div className="flex items-center gap-3">
          <span className="text-zinc-500 text-sm">
            {sportStandings.teams?.length || 0} teams
          </span>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-zinc-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-zinc-400" />
          )}
        </div>
      </button>
      
      {/* Expandable Content */}
      {isExpanded && (
        <div className="border-t border-white/10">
          {sportStandings.error ? (
            <div className="p-4 text-red-400 text-sm">
              Error loading standings: {sportStandings.error}
            </div>
          ) : sportStandings.teams?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                {/* Column Groups Header (if available) */}
                {sportStandings.column_groups?.length > 0 && (
                  <thead>
                    <tr className="border-b border-white/10">
                      {sportStandings.column_groups.map((group, idx) => (
                        <th 
                          key={idx}
                          colSpan={group.span}
                          className="px-4 py-2 text-center text-xs font-bold uppercase tracking-wider"
                          style={{ color: primaryColor }}
                        >
                          {group.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                )}
                
                {/* Regular Headers */}
                <thead>
                  <tr className="border-b border-white/10 text-zinc-400 bg-zinc-800/50">
                    {sportStandings.headers?.map((header, idx) => (
                      <th 
                        key={idx} 
                        className={`px-4 py-3 font-medium text-xs uppercase ${idx === 0 ? 'text-left' : 'text-center'}`}
                      >
                        {header || (idx === 0 ? 'Team' : '')}
                      </th>
                    ))}
                  </tr>
                </thead>
                
                {/* Team Rows */}
                <tbody>
                  {sportStandings.teams.map((team, idx) => (
                    <tr 
                      key={idx} 
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                    >
                      {team.raw_values?.map((value, cellIdx) => (
                        <td 
                          key={cellIdx} 
                          className={`px-4 py-3 ${cellIdx === 0 ? 'text-left text-white font-medium' : 'text-center text-zinc-300'}`}
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4 text-zinc-500 text-center">No teams found</div>
          )}
          
          {sportStandings.last_sync && (
            <div className="px-4 py-2 text-xs text-zinc-500 border-t border-white/5 flex items-center justify-between">
              <span>Last updated: {new Date(sportStandings.last_sync).toLocaleString()}</span>
              {sportStandings.source_url && (
                <a 
                  href={sportStandings.source_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  View source ↗
                </a>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Game Card Component
const GameCard = ({ game, primaryColor, selected, onClick }) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  return (
    <button
      onClick={onClick}
      className={`bg-zinc-900 rounded-lg p-4 text-left transition-all hover:bg-zinc-800 border-2 ${
        selected ? "border-opacity-100" : "border-transparent"
      }`}
      style={{ borderColor: selected ? primaryColor : "transparent" }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {game.away_team?.logo_url ? (
            <img src={game.away_team.logo_url} alt="" className="w-8 h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center text-xs text-white">
              {game.away_team?.abbreviation || "AWY"}
            </div>
          )}
          <span className="text-white text-sm font-medium truncate max-w-[80px]">
            {game.away_team?.abbreviation || game.away_team?.name || "Away"}
          </span>
        </div>
        <span className="text-zinc-500 text-xs">@</span>
        <div className="flex items-center gap-2">
          <span className="text-white text-sm font-medium truncate max-w-[80px]">
            {game.home_team?.abbreviation || game.home_team?.name || "Home"}
          </span>
          {game.home_team?.logo_url ? (
            <img src={game.home_team.logo_url} alt="" className="w-8 h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center text-xs text-white">
              {game.home_team?.abbreviation || "HME"}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 text-zinc-400 text-xs">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {formatDate(game.scheduled_date)}
        </span>
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatTime(game.scheduled_time)}
        </span>
      </div>
      <div className="mt-2">
        <span 
          className="text-xs px-2 py-0.5 rounded text-white font-medium"
          style={{ backgroundColor: primaryColor }}
        >
          {game.sport || "Basketball"}
        </span>
      </div>
      {game.is_live && (
        <div className="mt-2 text-xs font-bold uppercase flex items-center gap-1" style={{ color: primaryColor }}>
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: primaryColor }} />
          LIVE NOW
        </div>
      )}
    </button>
  );
};

// Score Card Component
const ScoreCard = ({ game, primaryColor }) => (
  <div className="bg-zinc-900 border border-white/10 p-4">
    <div className="flex items-center justify-between mb-2">
      <span 
        className="text-xs px-2 py-0.5 rounded text-white font-medium"
        style={{ backgroundColor: primaryColor }}
      >
        {game.sport || "Event"}
      </span>
      {game.is_live && (
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: primaryColor }} />
          <span className="text-xs font-bold uppercase" style={{ color: primaryColor }}>LIVE</span>
        </div>
      )}
    </div>
    <div className="flex items-center justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-2">
          {game.away_team?.logo_url ? (
            <img src={game.away_team.logo_url} alt="" className="w-8 h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 bg-zinc-700 rounded-full" />
          )}
          <span className="text-white font-medium">{game.away_team?.name || "Away"}</span>
          <span className="text-white text-xl font-bold ml-auto">{game.away_score || "-"}</span>
        </div>
        <div className="flex items-center gap-3">
          {game.home_team?.logo_url ? (
            <img src={game.home_team.logo_url} alt="" className="w-8 h-8 object-contain" />
          ) : (
            <div className="w-8 h-8 bg-zinc-700 rounded-full" />
          )}
          <span className="text-white font-medium">{game.home_team?.name || "Home"}</span>
          <span className="text-white text-xl font-bold ml-auto">{game.home_score || "-"}</span>
        </div>
      </div>
    </div>
    {game.is_live && game.quarter && (
      <div className="mt-2 text-zinc-400 text-sm text-center">
        {game.quarter || game.period || ""}
      </div>
    )}
  </div>
);

function extractYouTubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return match ? match[1] : null;
}

export default ConferenceNetworkPage;
